import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { createThemedStyles } from '../theme/useThemedStyles';
import WebAuthLayout from '../components/web/WebAuthLayout';
import AppMessageModal from '../components/AppMessageModal';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getAuthErrorMessage } from '../utils/authErrors';
import { isWeb } from '../utils/platformUtils';

interface ForgotPasswordScreenProps {
  onBack: () => void;
}

const ForgotPasswordScreen = ({ onBack }: ForgotPasswordScreenProps) => {
  const { resetPassword } = useAuth();
  const { colors } = useTheme();
  const styles = useForgotPasswordStyles();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageModal, setMessageModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    variant: 'success' | 'error' | 'info';
    onConfirm?: () => void;
  }>({ visible: false, title: '', message: '', variant: 'info' });

  const closeMessageModal = () => {
    const onConfirm = messageModal.onConfirm;
    setMessageModal(prev => ({ ...prev, visible: false }));
    onConfirm?.();
  };

  const showMessage = (
    title: string,
    message: string,
    variant: 'success' | 'error' | 'info',
    onConfirm?: () => void
  ) => {
    setMessageModal({ visible: true, title, message, variant, onConfirm });
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      showMessage('Error', 'Please enter your email address', 'error');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      showMessage('Error', 'Please enter a valid email address', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await resetPassword(email);
      showMessage(
        'Success',
        'If an account exists with this email, you will receive password reset instructions.',
        'success',
        onBack
      );
    } catch (error) {
      showMessage('Error', getAuthErrorMessage(error, 'Failed to send reset instructions. Please try again.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const formBody = (
    <>
      <Text style={isWeb ? styles.descriptionWeb : styles.description}>
        Enter your email address and we'll send you instructions to reset your password.
      </Text>

      <View style={isWeb ? styles.inputGroupWeb : undefined}>
        {isWeb ? <Text style={styles.inputLabel}>Email</Text> : null}
        <TextInput
          style={isWeb ? styles.inputWeb : styles.input}
          placeholder="Email"
          placeholderTextColor={colors.text.placeholder}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          editable={!isLoading}
        />
      </View>

      <TouchableOpacity
        style={[styles.button, isLoading && styles.buttonDisabled]}
        onPress={handleResetPassword}
        disabled={isLoading}
      >
        {isLoading ? (
          <ActivityIndicator color="white" />
        ) : (
          <Text style={styles.buttonText}>Send Reset Instructions</Text>
        )}
      </TouchableOpacity>
    </>
  );

  const messageDialog = (
    <AppMessageModal
      visible={messageModal.visible}
      title={messageModal.title}
      message={messageModal.message}
      variant={messageModal.variant}
      onConfirm={closeMessageModal}
    />
  );

  if (isWeb) {
    return (
      <>
        <WebAuthLayout
          cardTitle="Reset password"
          cardSubtitle="We'll email you a link to choose a new password"
          onBack={onBack}
        >
          {formBody}
        </WebAuthLayout>
        {messageDialog}
      </>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          <View style={styles.header}>
            <TouchableOpacity onPress={onBack} style={styles.backButton}>
              <Ionicons name="arrow-back" size={24} color="white" />
            </TouchableOpacity>
            <Text style={styles.title}>Reset Password</Text>
          </View>

          <View style={styles.formContainer}>
            {formBody}
          </View>
        </View>
      </ScrollView>
      {messageDialog}
    </SafeAreaView>
  );
};

const useForgotPasswordStyles = createThemedStyles((colors) => ({
  safeArea: {
    flex: 1,
    backgroundColor: colors.primary.header,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 40,
  },
  backButton: {
    padding: 8,
    marginRight: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  formContainer: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    padding: 20,
    width: '100%',
  },
  description: {
    fontSize: 16,
    color: colors.text.secondary,
    marginBottom: 24,
    lineHeight: 24,
  },
  descriptionWeb: {
    fontSize: 15,
    color: colors.text.secondary,
    marginBottom: 20,
    lineHeight: 22,
  },
  inputGroupWeb: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.secondary,
    marginBottom: 8,
  },
  input: {
    backgroundColor: colors.grey[100],
    borderRadius: 12,
    padding: 15,
    marginBottom: 16,
    fontSize: 16,
    color: colors.text.primary,
  },
  inputWeb: {
    fontSize: 16,
    color: colors.text.primary,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 14,
    width: '100%',
    outlineStyle: 'none' as const,
  },
  button: {
    backgroundColor: colors.button.primary,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  buttonDisabled: {
    backgroundColor: colors.grey[300],
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
}));

export default ForgotPasswordScreen;
